import CloseIcon from '@mui/icons-material/Close';
import { Alert, AlertTitle, IconButton, Slide, Snackbar } from '@mui/material';

import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import { shadow } from '@/theme/tokens';

import { selectToasts, toastDismissed, type ToastSeverity } from './ui.slice';

const SEVERITY_TITLES: Record<ToastSeverity, string> = {
  success: 'Success',
  error: 'Error',
  warning: 'Warning',
  info: 'Info',
};

/** Success and error read as light cards with a coloured edge; the other two
 *  stay filled, where the severity colour itself is the signal. */
const isOutlined = (severity: ToastSeverity) => severity === 'success' || severity === 'error';

/**
 * Renders the toast queue. Mounted once, near the root.
 *
 * Only the first toast is shown: stacking snackbars covers content on a phone,
 * and a queue the user cannot dismiss fast enough is worse than one at a time.
 */
export const ToastHost = () => {
  const dispatch = useAppDispatch();
  const toasts = useAppSelector(selectToasts);
  const current = toasts[0];

  const dismiss = () => {
    if (current === undefined) return;
    dispatch(toastDismissed(current.id));
  };

  return (
    <Snackbar
      open={current !== undefined}
      autoHideDuration={5000}
      onClose={(_event, reason) => {
        // Ignore click-away on touch: a stray tap should not eat an error the
        // user has not read yet.
        if (reason === 'clickaway') return;
        dismiss();
      }}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      slots={{ transition: (props) => <Slide {...props} direction="left" /> }}
      // `top-header` clears the fixed header and the notch it pads itself by.
      // Full-bleed on a phone so the message is not squeezed into a corner.
      className="inset-x-4 top-header mt-2 md:inset-x-auto md:right-6"
    >
      {current === undefined ? undefined : (
        <Alert
          severity={current.severity}
          variant={isOutlined(current.severity) ? 'outlined' : 'filled'}
          action={
            <IconButton
              size="small"
              aria-label="Dismiss notification"
              color="inherit"
              onClick={dismiss}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
          className="w-full items-start md:w-80"
          sx={{
            borderRadius: 2,
            boxShadow: shadow.menu,
            '& .MuiAlert-message': { flex: 1, minWidth: 0 },
            '& .MuiAlert-action': { pt: 0.5 },
            ...(isOutlined(current.severity) && {
              bgcolor: 'background.paper',
              color: 'text.primary',
              borderColor: `${current.severity}.light`,
              '& .MuiAlert-icon': { color: `${current.severity}.main` },
              '& .MuiAlertTitle-root': { color: `${current.severity}.dark` },
            }),
          }}
        >
          <AlertTitle sx={{ fontWeight: 700, mb: 0.25 }}>
            {SEVERITY_TITLES[current.severity]}
          </AlertTitle>
          {current.message}
        </Alert>
      )}
    </Snackbar>
  );
};
